<template>
	<div :style="style">
		<VideoPlayer
			v-if="value.data.video"
      :player-id="`videoPlayer-${value.id}`"
      :sources="{ src: options.sources.src, type: options.sources.type }"
      :options="options"
			:loop="value.data.loop"
			:fluid="false"
			:disable-progress-bar="disableProgressBar"
			:autoplay-delayed="autoplayDelayed"
			:autoplay-delayed-ms="autoplayDelayedMs"
			:are-subtitles-active="areSubtitlesActive"
			:subtitles="videoSubtitles"
			:selected-subtitles-language="selectedSubtitlesLanguage"
			:available-subtitles-language="availableSubtitlesLang"
			width="100%"
			video-class="p-block-video-viewer vjs-big-play-centered video-js"
      @ready="onReady"
      @ended="onEnded"
			@playbackCycleCompleted.once="onEnded"
			@updateSubtitlesState="updateSubtitlesState"
    />
	</div>
</template>

<script>
import { RevisionDisplayMode } from 'plant-common/src/models/revisionDisplayMode.js';
import { isPlayerProgressBarBlocked } from "plant-common/src/utils/mediaPlayer.js";
import { availableLanguages } from './availableLanguages.js';
import VideoPlayer from 'plant-common/src/components/VideoPlayer';

export default {
	name: 'BlockVideoViewer',
	props: ['value', 'mode'],
	inject: ['lessonPlanController'],
	components: { VideoPlayer },
	data() {
		return {
			availableSubtitlesLang: availableLanguages,
			disableProgressBar: false,
			autoplayDelayed: false,
			autoplayDelayedMs: 1000,
			areSubtitlesActive: false,
			selectedSubtitlesLanguage: 'en'
		};
	},
	computed: {
		style() {
			return {
				'--audio-video-controls-color': this.value.data.controls.color,
				'--audio-video-controls-size': `${this.value.data.controls.size}px`,
				'--audio-video-controls-button-zoom': this.value.data.controls.size / 25
			};
		},
		options() {
			return {
				controls: true,
				width: this.value.size.width,
				height: this.value.size.height,
				sources: [
					{
						src: this.value.data.video ? this.value.data.video.url : null,
						type: this.value.data.video ? this.value.data.video.type : null
					}
				]
			};
		},
		videoSubtitles() {
			return this.value.data.subtitles;
		},
		subtitlesActive() {
			return this.lessonPlanController.subtitlesActive;
		},
		subtitlesLang() {
			return this.lessonPlanController.defaultSubtitles;
		},
		isBlockVisited() {
			return this.value.data.visited;
		},
		isEditMode() {
			return this.lessonPlanController.mode === RevisionDisplayMode.edit;
		},
		parentSubcanvasDelay() {
			return this.$parent.$parent.$attrs.subcanvasDelay ? this.$parent.$parent.$attrs.subcanvasDelay : 0;
		}
	},
	mounted() {
		if (this.value.data.autoplay && this.mode == 'view') {
			this.autoplayDelayed = true;
			this.autoplayDelayedMs = (this.value.data.delay + this.parentSubcanvasDelay) * 1000;
		}

    const hasVideoSource = Boolean(this.value.data.video)

    if (!this.isEditMode && !hasVideoSource) this.$set(this.value.data, 'visited', true);
	},
	methods: {
		onReady() {
			if (isPlayerProgressBarBlocked(this.lessonPlanController, this.isBlockVisited)) {
				this.disableProgressBar = true;
			}
		},
		onEnded() {
			if (!this.isEditMode) {
				this.$set(this.value.data, 'visited', true);
			}

			this.disableProgressBar = false;
		},
		updateSubtitlesState({ active, language }) {
			this.lessonPlanController.subtitlesActive = active
			this.lessonPlanController.defaultSubtitles = language
		}
	},
	watch: {
		subtitlesActive: {
			handler() {
				this.areSubtitlesActive = this.subtitlesActive;
			},
			immediate: true
		},
		subtitlesLang: {
			handler() {
				if (!this.subtitlesLang) return;
				
				this.selectedSubtitlesLanguage = this.subtitlesLang;
			},
			immediate: true
		},
    "lessonPlanController.mode": {
      handler() {
        if (isPlayerProgressBarBlocked(this.lessonPlanController, this.isBlockVisited)) {
					this.disableProgressBar = true;
          return;
        }

				this.disableProgressBar = false;
      }
    }
	}
};
</script>

<style>
.p-block-video-viewer.video-js {
	color: var(--audio-video-controls-color);
}

/* Change the border of the big play button. */
.p-block-video-viewer .vjs-big-play-button {
	border-color: var(--audio-video-controls-color);
	zoom: var(--audio-video-controls-button-zoom);
}

/* Change the color of various "bars". */
.p-block-video-viewer .vjs-volume-level,
.p-block-video-viewer .vjs-play-progress,
.p-block-video-viewer .vjs-slider-bar {
	background: var(--audio-video-controls-color);
}
.p-block-video-viewer .vjs-control-bar {
	font-size: var(--audio-video-controls-size);
}

.p-block-video-viewer .vjs-text-track-cue {
	inset: 90% 0 0 0 !important;
	height: 10% !important;
	width: 97% !important;
}
</style>
